/*
Redux slice for requests / information about a specific order.
*/
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { LOADING, SUCCEEDED, FAILED, IDLE } from '@constants/fetchStatuses'
import OrdersManager from '../../apis/orders/OrdersManager'

const initialState = {
  acceptRequirements: {
    status: IDLE,
    error: null,
    data: {},
  },
  eligibleAppraisers: {
    status: IDLE,
    error: null,
    data: {},
  },
  assistedAssignmentInfo: {
    status: IDLE,
    error: null,
    data: {},
  },
  eligibleAppraiserProximities: {
    status: IDLE,
    error: null,
    data: {},
  },
  inspectionAvailability: {
    status: IDLE,
    error: null,
    data: {},
  },
}

export const fetchAcceptRequirements = createAsyncThunk(
  'orderInfo/fetchAcceptRequirements',
  async (orderId) => {
    const response = await OrdersManager.getAcceptRequirements(orderId)
    return response
  },
)

export const fetchEligibleAppraisers = createAsyncThunk(
  'orderInfo/fetchEligibleAppraisers',
  async (orderId) => {
    const response = await OrdersManager.getEligibleAppraisers(orderId)
    return response
  },
)

export const fetchAssistedAssignmentInfo = createAsyncThunk('orderInfo/fetchAssistedAssignmentInfo', async ({
  orderId,
  params,
}) => {
  const response = await OrdersManager.getAssistedAssignmentInfo(orderId, params)
  return response
})

export const fetchEligibleAppraiserProximities = createAsyncThunk(
  'orderInfo/fetchEligibleAppraiserProximities',
  async (orderId) => {
    const response = await OrdersManager.getEligibleAppraiserProximity(orderId)
    return response
  },
)

export const fetchInspectionAvailability = createAsyncThunk(
  'orderInfo/fetchInspectionAvailability',
  async (orderId) => {
    const response = await OrdersManager.getInspectionAvailability(orderId)
    return response
  },
)

const orderInfoSlice = createSlice({
  name: 'orderInfo',
  initialState,
  reducers: {
    completeRequirements: (state, action) => {
      const { orderId, completedRequirements } = action.payload
      const currentRequirements = state.acceptRequirements.data[orderId] || {}
      state.acceptRequirements.data[orderId] = Object.keys(currentRequirements).reduce((acc, currentRequirement) => {
        acc[currentRequirement] = {
          completed: completedRequirements.includes(currentRequirement),
        }
        return acc
      }, {})
    },
  },
  extraReducers: {
    [fetchAcceptRequirements.pending]: (state) => {
      state.acceptRequirements.status = LOADING
    },
    [fetchAcceptRequirements.fulfilled]: (state, action) => {
      const orderId = action.meta.arg
      state.acceptRequirements.status = SUCCEEDED
      const { accept_requirements: acceptRequirements } = action.payload
      state.acceptRequirements.data[orderId] = acceptRequirements.reduce((acc, currentRequirement) => ({
        ...acc,
        [currentRequirement]: { completed: false },
      }), {})
    },
    [fetchAcceptRequirements.rejected]: (state, action) => {
      state.acceptRequirements.status = FAILED
      state.acceptRequirements.error = action.error.message
    },
    [fetchEligibleAppraisers.pending]: (state) => {
      state.eligibleAppraisers.status = LOADING
    },
    [fetchEligibleAppraisers.fulfilled]: (state, action) => {
      const orderId = action.meta.arg
      state.eligibleAppraisers.status = SUCCEEDED
      state.eligibleAppraisers.data[orderId] = action.payload
    },
    [fetchEligibleAppraisers.rejected]: (state, action) => {
      state.eligibleAppraisers.status = FAILED
      state.eligibleAppraisers.error = action.error.message
    },
    [fetchAssistedAssignmentInfo.pending]: (state, action) => {
      state.assistedAssignmentInfo.status = LOADING
      state.assistedAssignmentInfo.data = {}
    },
    [fetchAssistedAssignmentInfo.fulfilled]: (state, action) => {
      state.assistedAssignmentInfo.status = SUCCEEDED
      state.assistedAssignmentInfo.data = action.payload
    },
    [fetchAssistedAssignmentInfo.rejected]: (state, action) => {
      state.assistedAssignmentInfo.status = FAILED
      state.assistedAssignmentInfo.error = action.error.message
    },
    [fetchEligibleAppraiserProximities.pending]: (state) => {
      state.eligibleAppraiserProximities.status = LOADING
    },
    [fetchEligibleAppraiserProximities.fulfilled]: (state, action) => {
      const orderId = action.meta.arg
      state.eligibleAppraiserProximities.status = SUCCEEDED
      state.eligibleAppraiserProximities.data[orderId] = action.payload
    },
    [fetchEligibleAppraiserProximities.rejected]: (state, action) => {
      state.eligibleAppraiserProximities.status = FAILED
      state.eligibleAppraiserProximities.error = action.error.message
    },
    [fetchInspectionAvailability.pending]: (state) => {
      state.inspectionAvailability.status = LOADING
    },
    [fetchInspectionAvailability.fulfilled]: (state, action) => {
      const orderId = action.meta.arg
      state.inspectionAvailability.status = SUCCEEDED
      state.inspectionAvailability.data[orderId] = {
        inspection_availability: action.payload.inspection_availability,
        timezone: action.payload.timezone,
      }
      state.inspectionAvailability.error = null
    },
    [fetchInspectionAvailability.rejected]: (state, action) => {
      state.inspectionAvailability.status = FAILED
      state.inspectionAvailability.error = action.error.message
    },
  },
})

export const { completeRequirements } = orderInfoSlice.actions

export default orderInfoSlice.reducer
